// Two taps to get to work: which line, then which run on it — or start one.
//
// ⚠ THIS SCREEN USED TO REFUSE TO START A RUN, AND THE REASON IT NOW DOES IS
// WORTH KEEPING. The original argument was that starting a run fixes
// Run.SpecVersionId — the specification every reading on it will be judged
// against for its whole life — so it is "a decision with an approver behind it,
// not something an operator does because the screen offered a button".
//
// Half of that still holds and is why the control below is shaped the way it is.
// The other half did not survive slice 1b. Start-up and changeover checks are
// obligations ON A RUN, so the run has to exist before the walk-around can
// begin — and the walk-around happens at changeover, which on this site is
// routinely 2am. An office-only start meant the line waits for someone who is
// not there, and the predictable result of that is the paper form being filled
// in later, which is the exact failure this app exists to remove.
//
// What keeps the original concern answered:
//   · The operator does NOT choose a specification. The server resolves whatever
//     version is APPROVED and in force at that instant; there is no version
//     picker here and there must never be one.
//   · The first-off gate still refuses a spec that declares no start-up checks,
//     server-side, and the refusal is shown verbatim rather than swallowed.
//   · Run.StartedByUpn records who did it, from the token.
//   · Nothing is released by starting a run. Readiness obligations are created
//     OWED, and the capture gate refuses product checks until they are met.
//     Starting a run is now the least consequential thing on this screen.
//
// Route contract: picking a run navigates to /op/run/:runId, which LineCheck reads
// with useParams. If routes.ts ever renames that parameter, both files move.
//
// ⚠ THE CHOSEN LINE IS IN THE URL, `/op?line=CODE` (UI/UX audit, 31 Oct 2026, UX-52). It
// was component state only, so a reload, or Back from a run, dropped the operator on
// "Which line are you on?" again. The URL is now the one source: tapping a line or
// "Change line" writes it, and this screen follows it. LineCheck's and Verify's "‹ Lines"
// link here with the run's line. A code no active line has is ignored: the picker shows.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
// Rule 17's "no check for more than a shift", the SAME function the office Runs register reads
// (UI/UX audit, 31 Oct 2026: this file's private copy deleted; two copies of a rule drift).
import { lastActivity, quietLongerThanAShift } from '../../lib/runQuiet';
import { PilotBadge } from '../../components/Pilot';
import { opFocus, opMessage } from '../../shells/OperatorShell';

/** GET /lines — id, code, name, areaId and the area's code and name. There is no
 *  `isActive` field: the route filters `WHERE IsActive = 1` itself, so every row
 *  that arrives is active. Filtering on a column the API does not send empties
 *  the grid and leaves an operator looking at a screen with no lines on it. */
interface Line {
  id: number;
  code: string;
  name: string;
  areaName?: string | null;
}

/** GET /runs?lineId=&status=OPEN — a flat row per run. `specVersion` is the
 *  version NUMBER, not an object. */
interface OpenRun {
  id: number;
  lineId: number;
  productId: number;
  productCode?: string | null;
  productName?: string | null;
  d365BatchNumber?: string | null;
  startedAt: string;
  status: 'OPEN' | 'CLOSED';
  /** The newest reading's capture time, or null when nothing has been captured. */
  lastReadingAt?: string | null;
  /** Started in pilot mode (rule 27): not a controlled record. */
  isPilot?: boolean;
}

/** GET /products?lineId= — the products a run can be started against ON THAT LINE.
 *
 *  ⚠ THE `lineId` IS NOT OPTIONAL HERE EVEN THOUGH THE ROUTE ALLOWS IT. Asking
 *  for the unfiltered list is how this screen offered Gathia on the Extrusion
 *  line: four runs were attempted that way on 2026-09-17, two of them against
 *  products fried on a different line. The server refuses that now too
 *  (`product_not_on_line`), but a picker that offers a choice the server will
 *  reject is a trap, not a safeguard. */
interface Product {
  id: number;
  code: string;
  name: string;
}

/** POST /runs — what comes back when one is started. */
interface StartedRun {
  id: number;
  obligations?: { firstOff: number; adherence: number; readiness: number };
}

const CARD =
  'flex min-h-[96px] w-full flex-col items-start justify-center gap-(--s1) rounded-(--radius) ' +
  'border border-(--border-strong) bg-(--card) px-(--s4) py-(--s3) text-left ' +
  'active:bg-(--primary-soft)';

function startedAtLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function LinePick() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const lineParam = searchParams.get('line');
  const [lines, setLines] = useState<Line[] | null>(null);
  const [line, setLine] = useState<Line | null>(null);
  const [runs, setRuns] = useState<OpenRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Starting a run. `starting` is the guard against a double tap on a gloved
  // thumb producing two runs on one line — the server would accept both, and
  // two open runs on a line is a mess an operator cannot untangle from here.
  const [showStart, setShowStart] = useState(false);
  const [products, setProducts] = useState<Product[] | null>(null);
  // Which line the loaded product list belongs to. Without this the list is
  // cached across a line change, and the operator who switches from Packing to
  // the Fryer is still being offered Packing's twelve mixes.
  const [productsForLine, setProductsForLine] = useState<number | null>(null);
  const [productId, setProductId] = useState<number | null>(null);
  const [batch, setBatch] = useState('');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const loadLines = useCallback(() => {
    setError(null);
    setLines(null);
    api
      .get('/lines')
      .then((d: unknown) => setLines(Array.isArray(d) ? (d as Line[]) : []))
      .catch((e: unknown) => setError(opMessage(e)));
  }, []);

  useEffect(loadLines, [loadLines]);

  // The line whose runs we are waiting for. A second tap must win: on a shared
  // dock, picking line 3, going back and picking line 5 can land line 3's runs
  // last, and the operator would then open a run on a line they are not standing
  // at — with a header that says line 5.
  const awaiting = useRef<number | null>(null);

  const openStart = useCallback(() => {
    if (!line) return;
    setShowStart(true);
    setStartError(null);
    setProductId(null);
    setBatch('');
    if (products !== null && productsForLine === line.id) return;

    const wanted = line.id;
    setProducts(null);
    api
      .get(`/products?lineId=${wanted}`)
      .then((d: unknown) => {
        // A slower response for the line the operator has since left must not
        // land: the same last-write-wins problem `awaiting` solves for runs.
        if (awaiting.current !== wanted) return;
        setProducts(Array.isArray(d) ? (d as Product[]) : []);
        setProductsForLine(wanted);
      })
      .catch((e: unknown) => {
        if (awaiting.current !== wanted) return;
        setStartError(opMessage(e));
      });
  }, [line, products, productsForLine]);

  /** An open run on THIS line for the product currently selected. Null when the
   *  operator has picked nothing yet, which is the usual state. */
  const duplicateOfOpenRun =
    productId === null ? null : ((runs ?? []).find((r) => r.productId === productId) ?? null);

  const startRun = useCallback(() => {
    if (!line || productId === null || starting) return;
    setStarting(true);
    setStartError(null);
    api
      .post('/runs', {
        lineId: line.id,
        productId,
        // Trimmed to null rather than sent as an empty string: the column is
        // nullable and "" would read as a batch number that is blank.
        d365BatchNumber: batch.trim() === '' ? null : batch.trim(),
      })
      .then((d: unknown) => {
        const run = d as StartedRun;
        // Straight into the run. The operator started it because they are about
        // to work on it, and the first thing waiting there is the start-up walk.
        navigate(`/op/run/${run.id}`);
      })
      .catch((e: unknown) => {
        // The server's sentence, verbatim. "No approved product specification is
        // in force" and "this specification declares no first-off checks" are
        // different problems with different people to go to, and flattening them
        // into "could not start run" throws away the only useful part.
        setStartError(opMessage(e));
        setStarting(false);
      });
  }, [line, productId, batch, starting, navigate]);

  /** Back to the list of lines, dropping any run list still on its way. */
  const leaveLine = useCallback(() => {
    // Also drops the in-flight request: a run list arriving after the
    // operator has gone back to the line picker belongs to nothing.
    awaiting.current = null;
    setLine(null);
    setRuns(null);
    setError(null);
  }, []);

  const chooseLine = useCallback((chosen: Line) => {
    awaiting.current = chosen.id;
    setLine(chosen);
    setRuns(null);
    setError(null);
    setShowStart(false);
    api
      .get(`/runs?lineId=${chosen.id}&status=OPEN`)
      .then((d: unknown) => {
        if (awaiting.current !== chosen.id) return;
        setRuns(Array.isArray(d) ? (d as OpenRun[]) : []);
      })
      .catch((e: unknown) => {
        if (awaiting.current !== chosen.id) return;
        setError(opMessage(e));
      });
  }, []);

  // The URL drives the choice: a tap writes `?line=`, and this follows it - on load, on a
  // reload, and on Back (which empties it again).
  useEffect(() => {
    if (lineParam === null) {
      if (line) leaveLine();
      return;
    }
    if (lines === null || line?.code === lineParam) return;
    const found = lines.find((l) => l.code === lineParam);
    if (found) chooseLine(found);
    else if (line) leaveLine();
  }, [lineParam, lines, line, chooseLine, leaveLine]);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-(--s5)">
      <h1 className="m-0 text-(length:--fs-xl) font-bold">
        {line ? `Open runs on ${line.name}` : 'Which line are you on?'}
      </h1>

      {line ? (
        <button
          type="button"
          onClick={() => setSearchParams({})}
          className={`mt-(--s3) min-h-[56px] w-fit rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(length:--fs-md) font-semibold ${opFocus}`}
        >
          Change line
        </button>
      ) : null}

      {error ? (
        <div
          role="alert"
          className="mt-(--s4) rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) p-(--s4) text-(length:--fs-md) font-semibold text-(--warn)"
        >
          {error}
          <button
            type="button"
            onClick={line ? () => chooseLine(line) : loadLines}
            className={`ml-(--s3) min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(--fg) ${opFocus}`}
          >
            Try again
          </button>
        </div>
      ) : null}

      <div className="mt-(--s4) min-h-0 flex-1 overflow-y-auto">
        {/* ⚠ SAY THAT WE ARE ASKING (UI/UX audit, 31 Oct 2026, UX-10). While lines or
            runs were loading this screen rendered nothing at all, and an empty
            screen on a docked iPad reads as "no lines" or "no runs" - a claim
            nobody made (rule 15). A failed read already has its own alert and
            Try again above, so the status shows only while a read is in flight. */}
        {!error && !line && lines === null ? (
          <p role="status" className="m-0 text-(length:--fs-md) text-(--muted)">
            Loading lines…
          </p>
        ) : null}
        {!error && line && runs === null ? (
          <p role="status" className="m-0 text-(length:--fs-md) text-(--muted)">
            Loading runs on {line.name}…
          </p>
        ) : null}
        {!line ? (
          <ul className="m-0 grid list-none grid-cols-2 gap-(--s3) p-0">
            {(lines ?? []).map((l) => (
              <li key={l.id}>
                <button type="button" onClick={() => setSearchParams({ line: l.code })} className={`${CARD} ${opFocus}`}>
                  <span className="text-(length:--fs-lg) font-bold">{l.name}</span>
                  <span className="font-mono text-(length:--fs-sm) text-(--muted)">
                    {l.code}
                  </span>
                </button>
              </li>
            ))}
            {lines && lines.length === 0 && !error ? (
              <li className="text-(length:--fs-md) text-(--muted)">
                No active lines are configured.
              </li>
            ) : null}
          </ul>
        ) : (
          <>
            <ul className="m-0 grid list-none grid-cols-2 gap-(--s3) p-0">
              {(runs ?? []).map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/op/run/${r.id}`)}
                    className={`${CARD} ${opFocus}`}
                  >
                    <span className="flex items-center gap-(--s2) text-(length:--fs-lg) font-bold">
                      {r.productName ?? r.productCode ?? `Product ${r.productId}`}
                      <PilotBadge isPilot={r.isPilot} />
                    </span>
                    <span className="font-mono text-(length:--fs-sm) text-(--muted)">
                      Run {r.id}
                      {r.d365BatchNumber ? ` · batch ${r.d365BatchNumber}` : ''} · started{' '}
                      {startedAtLabel(r.startedAt)}
                    </span>
                    {quietLongerThanAShift(r) !== null ? (
                      <span className="text-(length:--fs-sm) font-semibold text-(--warn)">
                        No check for more than a shift — is it still running?
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>

            {runs && runs.length === 0 && !error && !showStart ? (
              <div className="rounded-(--radius) border border-(--border) bg-(--card) p-(--s5)">
                <p className="m-0 text-(length:--fs-lg) font-bold">
                  There is no open run on {line.name}.
                </p>
                <p className="m-0 mt-(--s2) text-(length:--fs-md) text-(--muted)">
                  Checks are captured against a run. Starting one fixes the approved specification
                  this product will be judged against, and creates the start-up checks this line
                  owes before it can run.
                </p>
              </div>
            ) : null}

            {/* Offered whether or not runs already exist: a changeover starts a
                new run on a line that already has one open, and the operator
                doing the changeover is the person standing here. */}
            {runs && !error && !showStart ? (
              <button
                type="button"
                onClick={openStart}
                className={`mt-(--s4) min-h-[56px] w-full rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(length:--fs-lg) font-bold ${opFocus}`}
              >
                Start a run on {line.name}
              </button>
            ) : null}

            {showStart ? (
              <div className="mt-(--s4) rounded-(--radius) border border-(--border-strong) bg-(--card) p-(--s5)">
                <h2 className="m-0 text-(length:--fs-lg) font-bold">
                  Start a run on {line.name}
                </h2>
                <p className="m-0 mt-(--s2) text-(length:--fs-md) text-(--muted)">
                  The specification is not chosen here. Whichever version is approved and in force
                  right now is pinned to this run, and every reading on it is judged against that
                  one for its whole life.
                </p>

                <fieldset className="mt-(--s4) m-0 border-0 p-0">
                  <legend className="p-0 text-(length:--fs-md) font-semibold">
                    Which product? <span className="font-normal text-(--muted)">
                      Only what {line.name} runs.
                    </span>
                  </legend>
                  <ul className="m-0 mt-(--s3) grid list-none grid-cols-2 gap-(--s3) p-0">
                    {(products ?? []).map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          aria-pressed={productId === p.id}
                          onClick={() => setProductId(p.id)}
                          className={`${CARD} ${opFocus} ${
                            productId === p.id
                              ? 'border-(--primary) bg-(--primary-soft)'
                              : ''
                          }`}
                        >
                          <span className="text-(length:--fs-lg) font-bold">{p.name}</span>
                          <span className="font-mono text-(length:--fs-sm) text-(--muted)">
                            {p.code}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  {products === null && !startError ? (
                    <p role="status" className="m-0 mt-(--s3) text-(length:--fs-md) text-(--muted)">
                      Loading products…
                    </p>
                  ) : null}
                  {products && products.length === 0 ? (
                    <p className="m-0 mt-(--s3) text-(length:--fs-md) text-(--muted)">
                      No products are configured for {line.name}.
                    </p>
                  ) : null}
                </fieldset>

                {/* ⚠ TWO OPEN RUNS ON ONE LINE IS LEGAL — a changeover starts a
                    new run while the old one is still open, and that is why the
                    Start button is offered unconditionally. Two open runs on the
                    same line FOR THE SAME PRODUCT is almost always a second tap
                    by somebody who did not recognise the run card above. It is
                    not refused, because occasionally it is meant; it is said out
                    loud, because the cost is invisible: readings land on the new
                    run, the old one keeps its obligations, and every one of them
                    is booked MISSED the moment it is closed.

                    ⚠ RUNS CARRY ON ACROSS SHIFT CHANGES (Amit, 2026-09-24), so
                    "go back and open it" is usually right even for a run started
                    on an earlier shift. The exception is a run nobody has checked
                    for more than a shift: it has probably stopped and somebody
                    forgot to close it. Then the operator is told both options,
                    because only the person at the line knows which it is. */}
                {duplicateOfOpenRun ? (
                  <p
                    role="alert"
                    className="m-0 mt-(--s4) rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) p-(--s3) text-(length:--fs-md) font-semibold text-(--warn)"
                  >
                    {quietLongerThanAShift(duplicateOfOpenRun) !== null
                      ? `Run ${duplicateOfOpenRun.id} for that product has had no check since ` +
                        `${startedAtLabel(new Date(lastActivity(duplicateOfOpenRun)).toISOString())} — ` +
                        'more than a shift. If that product is still running, go back and carry on in ' +
                        'it. If the line stopped, open it from the list above and close it, then start ' +
                        'this run.'
                      : `Run ${duplicateOfOpenRun.id} on this line is already open for that product, ` +
                        `started ${startedAtLabel(duplicateOfOpenRun.startedAt)}. Starting another ` +
                        'splits your checks across two runs. Go back and open the existing one unless ' +
                        'this is a genuine changeover.'}
                  </p>
                ) : null}

                <label className="mt-(--s4) block text-(length:--fs-md) font-semibold">
                  Batch number (optional)
                  <input
                    value={batch}
                    onChange={(e) => setBatch(e.target.value)}
                    inputMode="text"
                    autoComplete="off"
                    className="mt-(--s2) block min-h-[56px] w-full rounded-(--radius) border border-(--border-strong) bg-(--bg-input) px-(--s3) font-mono text-(length:--fs-lg)"
                  />
                </label>

                {startError ? (
                  <div
                    role="alert"
                    className="mt-(--s4) rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) p-(--s4) text-(length:--fs-md) font-semibold text-(--warn)"
                  >
                    {startError}
                  </div>
                ) : null}

                <div className="mt-(--s4) flex gap-(--s3)">
                  <button
                    type="button"
                    disabled={productId === null || starting}
                    onClick={startRun}
                    className={`min-h-[56px] flex-1 rounded-(--radius) border border-(--border-strong) bg-(--primary) px-(--s4) text-(length:--fs-lg) font-bold text-(--primary-fg) disabled:opacity-50 ${opFocus}`}
                  >
                    {starting ? 'Starting…' : 'Start run'}
                  </button>
                  <button
                    type="button"
                    disabled={starting}
                    onClick={() => setShowStart(false)}
                    className={`min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(length:--fs-md) font-semibold disabled:opacity-50 ${opFocus}`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export default LinePick;
